import { useState } from "react";
import { accountService, subscriptionService } from "@miplatform/api";
import {
  HTTP_STATUSES,
  INVOICE_STATUSES,
  PRINT_ORIENTATION,
} from "@miplatform/constants";
import { useComponentState, useDebounce } from "@miplatform/hooks";
import { IInvoiceResponse } from "@miplatform/types";
import { buildSearchQuery } from "@miplatform/utils";
import { usePrint, useSession } from "@mibuildweb/hooks";
import { OPEN_INVOICES_TAB } from "./constants";

interface GetAllInvoicesResponse {
  status: number;
  data?: IInvoiceResponse[];
  [key: string]: any;
}

export const useAccountsReceivableViewModel = ({ tab }: { tab: string }) => {
  const [invoices, setInvoices] = useState<IInvoiceResponse[]>([]);

  const [tenant, setTenant] = useState<any>(null);

  const [search, setSearch] = useState<string>("");

  const debouncedSearch = useDebounce(search, 500);
  const { handlePrintCustomJsx } = usePrint(tenant);

  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();

  const {
    session: { tenantId },
  } = useSession();

  const getTenant = async () => {
    await stateManager.run(async () => {
      const response = await subscriptionService.subscriptions.get(tenantId);

      if (response.status === HTTP_STATUSES.OK) {
        setTenant(response.data);
      }
    });
  };

  const getInvoices = ({
    top,
    skip,
    search,
    filters,
  }: {
    top?: number;
    skip?: number;
    search?: string;
    filters?: any;
  }): Promise<GetAllInvoicesResponse | null> => {
    return stateManager.run(async () => {
      const searchQuery = buildSearchQuery(search, [
        "invoiceNumber",
        "home.address.addressLine1",
      ]);

      const filterObj: any = {
        status: {
          eq:
            tab === OPEN_INVOICES_TAB
              ? INVOICE_STATUSES.INVOICED
              : INVOICE_STATUSES.PAID,
        },
        ...searchQuery,
      };

      if (filters) {
        if (filters.accountId) {
          filterObj.billToAccountId = {
            type: "guid",
            value: filters.accountId,
          };
        }
        if (filters.communityId) {
          filterObj["home/communityId"] = {
            type: "guid",
            value: filters.communityId,
          };
        }
        if (filters.syncType && filters.syncType !== "All") {
          filterObj.quickBooksType = { eq: filters.syncType };
        }
        if (filters.syncStatus && filters.syncStatus !== "All") {
          filterObj.syncStatus = { eq: filters.syncStatus };
        }
      }

      const response: GetAllInvoicesResponse =
        await accountService.invoices.getAll({
          filters: filterObj,
          top,
          skip,
          sort: filters?.sortBy ? filters.sortBy : "createdOn",
          enableCaching: false,
        });

      return response.status === HTTP_STATUSES.OK ? response : [];
    });
  };

  const handlePrint = (jsx: React.ReactNode | null) => {
    handlePrintCustomJsx(jsx, {
      orientation: PRINT_ORIENTATION.LANDSCAPE,
    });
  };

  return {
    isLoading,
    stateManager,
    search,
    invoices,
    setInvoices,
    getInvoices,
    debouncedSearch,
    setSearch,
    getTenant,
    handlePrint,
  };
};
